package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierBundleAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

class ModifierBundleExpandPass(private val diag: DiagnosticCollector) {

    fun expand(screen: ScreenAst): ScreenAst {
        val rootScope = BundleScope(null, LinkedHashMap())
        registerAll(rootScope, screen.theme?.modifierBundles.orEmpty())
        registerAll(rootScope, screen.modifierBundles)
        return screen.copy(
            root = expandElement(screen.root, rootScope),
            modifierBundles = emptyList(),
        )
    }

    private fun expandElement(element: ElementAst, inherited: BundleScope): ElementAst {
        val scope = BundleScope(inherited, LinkedHashMap())
        registerAll(scope, element.themeOverride?.modifierBundles.orEmpty())
        return element.copy(
            modifiers = expandModifiers(element.modifiers, scope, LinkedHashSet()),
            children = element.children.map { expandElement(it, scope) },
            conditionalContent = element.conditionalContent?.let { expandConditional(it, scope) },
            forContent = element.forContent?.let { expandFor(it, scope) },
        )
    }

    private fun expandConditional(content: ConditionalContentAst, scope: BundleScope): ConditionalContentAst =
        content.copy(
            branches = content.branches.map { it.copy(body = expandElement(it.body, scope)) },
            elseBranch = content.elseBranch?.let { expandElement(it, scope) },
        )

    private fun expandFor(content: ForContentAst, scope: BundleScope): ForContentAst =
        content.copy(body = expandElement(content.body, scope))

    private fun registerAll(scope: BundleScope, bundles: List<ModifierBundleAst>) {
        for (bundle in bundles) {
            if (scope.resolve(bundle.name) != null) {
                diag.error(DslError.AUI_V2_MODIFIER_BUNDLE_DUPLICATE, bundle.position, bundle.name)
            } else {
                scope.local[bundle.name] = bundle
            }
        }
    }

    private fun expandModifiers(
        modifiers: List<ModifierAst>,
        scope: BundleScope,
        stack: LinkedHashSet<String>,
    ): List<ModifierAst> {
        val out = mutableListOf<ModifierAst>()
        for (modifier in modifiers) {
            when {
                modifier.kind == "__group" -> {
                    out += expandModifiers(modifier.children, scope, stack)
                }
                modifier.useName != null -> {
                    out += expandUse(modifier, scope, stack)
                }
                else -> out += modifier
            }
        }
        return out
    }

    private fun expandUse(
        modifier: ModifierAst,
        scope: BundleScope,
        stack: LinkedHashSet<String>,
    ): List<ModifierAst> {
        val name = modifier.useName!!
        val bundle = scope.resolve(name)
        if (bundle == null) {
            diag.error(DslError.AUI_V2_MODIFIER_BUNDLE_UNRESOLVED, modifier.position, name)
            return emptyList()
        }
        if (!stack.add(name)) {
            diag.error(DslError.AUI_V2_MODIFIER_BUNDLE_CYCLE, modifier.position, (stack + name).joinToString(" -> "))
            return emptyList()
        }
        val expanded = expandModifiers(bundle.modifiers, scope, stack).map {
            if (modifier.condition == null || it.condition != null) it else it.copy(condition = modifier.condition)
        }
        stack.remove(name)
        return expanded
    }

    private class BundleScope(
        private val parent: BundleScope?,
        val local: LinkedHashMap<String, ModifierBundleAst>,
    ) {
        fun resolve(name: String): ModifierBundleAst? = local[name] ?: parent?.resolve(name)
    }
}
